/* eslint-disable react-hooks/exhaustive-deps */
// TODO: Better dependency pattern to avoid ^
import React, {
  FC,
  ReactElement,
  ReactNode,
  createContext,
  useState,
  useContext,
  useEffect,
} from 'react';
import qs from 'qs';
import { ApiRequest } from '../types';
import { useApi } from './useApi';
import { User } from '../models/user';

const TOKEN_KEY = 'ayble-auth-token';

// The design dashboard has no identity provider — `signIn()` below is an
// unimplemented TODO. Its "session" is entirely the fake token seeded in
// `src/index.tsx` plus the mocked `GET /private/provider/self` resolving, and
// `isAuthenticated` is just `!!userData`.
//
// When that call does not resolve, nothing errors loudly: `isAuthenticated`
// goes false, every `enabled: isAuthenticated && ...` query switches off, and
// the app renders as a signed-out shell — no provider name, zero appointments,
// zero tasks, every column falling through to its empty state. It looks like
// missing fixtures rather than a failed request, which makes it hard to spot.
//
// So under mocks we guarantee a user. The fixture is the same object the mock
// handler returns, imported dynamically so it is only pulled into mock builds.
const USE_MOCKS = process.env.REACT_APP_USE_MOCKS === 'true';

// Create the Auth Context with the data type specified
// and a empty object
const AuthContext = createContext({});

const AuthProvider: FC<{ children?: ReactNode }> = ({
  children,
}): ReactElement => {
  const [authData, setAuthData] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [userToken, setUserToken] = useState<any>(null);

  const { api } = useApi();

  // the AuthContext start with loading equals true
  // and stay like this, until the data be load from Async Storage
  const [loading, setLoading] = useState(true);

  const updateUserToken = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUserToken(token);
  };

  useEffect(() => {
    let findToken;

    setLoading(true);

    if (window.location.hash && window.location.hash.trim() !== '') {
      const hashData = qs.parse(window.location.hash.replace(/^#/, ''));

      if (hashData.error) {
        console.error('useAuth() - HASH ERROR:', hashData.error, hashData);
      } else if (hashData.access_token) {
        findToken = hashData.access_token;
        window.location.replace(window.location.pathname);
      }
    }

    if (!findToken && localStorage.getItem(TOKEN_KEY)) {
      findToken = localStorage.getItem(TOKEN_KEY);
    }

    if (findToken) {
      updateUserToken(findToken);
    } else {
      setLoading(false);
    }

    if (window.location?.hash?.trim() === '' && !findToken) {
      setLoading(false);
    }
  }, []);

  // Falls back to the seeded provider so the mock-backed app always has a user.
  const applyMockProvider = async () => {
    const { provider } = await import('../mocks/fixtures/provider');
    setUserData(provider);
  };

  useEffect(() => {
    const getUserMetadata = async () => {
      if (!userToken) {
        // Under mocks `src/index.tsx` always seeds a token before App mounts,
        // so this is the case where that seed did not run — localStorage
        // blocked, origin cleared mid-session. Still give the app a user.
        if (USE_MOCKS) {
          await applyMockProvider();
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        const userRequest: ApiRequest = {
          route: `/private/provider/self`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${userToken}`,
          },
        };

        const userResponse = await api(userRequest);
        setUserData(userResponse);
      } catch (e) {
        console.error('api error', (e as Error).message);
        // The mock layer not intercepting is the usual reason this fails — a
        // service worker that did not take control, or the app served without
        // the mocks flag. Either way the design dashboard should still render
        // its fixtures rather than silently degrade to an empty shell.
        if (USE_MOCKS) {
          console.warn(
            'useAuth: /private/provider/self did not resolve; falling back to the mock provider. If this persists, the MSW worker is probably not intercepting — hard-reload the page.'
          );
          await applyMockProvider();
        }
      } finally {
        setLoading(false);
      }
    };

    getUserMetadata();
  }, [userToken]);

  const signIn = async () => {
    // loginWithRedirect();
    // TODO: Hookup with final steps to send magic link from API
  };

  const signOut = async () => {
    // Remove data from context, so the App can be notified
    // and send the user to the AuthStack
    setAuthData(null);
    setUserData(null);
    setUserToken(null);
    localStorage.removeItem(TOKEN_KEY);
    // TODO: Refactor for navigate? (ERROR: Cannot use outside of <Router> context?)
    window.location.assign('/');
    // logout();
  };

  return (
    // This component will be used to encapsulate the whole App,
    // so all components will have access to the Context
    <AuthContext.Provider
      value={{
        authData,
        userToken,
        userData,
        user: userData,
        isAuthenticated: !!userData,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// A simple hooks to facilitate the access to the AuthContext
// and permit components to subscribe to AuthContext updates
function useAuth(): {
  user: User;
  userToken: string;
  authData: any;
  userData: any;
  isAuthenticated: boolean;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
} {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context as {
    user: any;
    userToken: string;
    authData: any;
    userData: any;
    isAuthenticated: boolean;
    loading: boolean;
    signIn: () => void;
    signOut: () => void;
  };
}

export { AuthContext, AuthProvider, useAuth };
